import { useCallback, useState } from "react";
import { SortColumn } from "react-data-grid";
import { useLocation } from "react-router-dom";

import { ArrowheadRightDarkIcon } from "../../assets";
import { TaskConstants } from "../../constants/workflow/TaskConstants";
import { CursorChangeProps, Origin } from "../../models";
import { IStateOrigin } from "../../navigators/navigateStateObjects";
import { searchWorkflowTasks, SearchWorkflowTasksResponse } from "../../service/query";
import { ResultData } from "../../service/Shared";
import { useAuth } from "../../useAuth";
import { flattenObject } from "../../utils";
import { getDealListingRoute, getProjectDetailsByUuid, getWorkflowTasksBoardRoute } from "../../utils/routes";
import {
  DataGridButtonLinkCellFormatterData,
  DataGridColumnDefinition,
  DataGridColumnDefinitionWithCustomCellFormatter,
  dataGridMapFilterCriteria,
} from "../../widget";
import { AvatarCellFormatter } from "../../widget/data/DataGrid/components/CellFormatters";
import { IconLinkCellFormatterData, LinkCellFormatterData } from "../../widget/data/DataGrid/models";

interface UseUpcomingMilestonesTaskPanelProps {
  projectUuid?: string;
  groupUuid?: string;
  workflowUuid?: string;
  isDashboard: boolean;
  setSelectedPanelTask: (task: SearchWorkflowTasksResponse["results"][0] | undefined) => void;
  setShowWorkflowTaskPanel: (show: boolean) => void;
}

interface UseUpcomingMilestonesTaskPanelReturnData {
  columns: DataGridColumnDefinition[];
  defaultSortingCriteria: SortColumn[];
  dataIsLoading: boolean;
  onChange: ({ filtering, paging, sorting }: CursorChangeProps) => Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }>;
}

export const useUpcomingMilestonesTaskPanel = ({
  projectUuid,
  groupUuid,
  workflowUuid,
  isDashboard,
  setSelectedPanelTask,
  setShowWorkflowTaskPanel,
}: UseUpcomingMilestonesTaskPanelProps): UseUpcomingMilestonesTaskPanelReturnData => {
  const location = useLocation();
  const { currentUserType } = useAuth();
  const [dataIsLoading, setDataIsLoading] = useState(true);

  const defaultSortingCriteria: SortColumn[] = [{ columnKey: "deadlineDate", direction: "ASC" }];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: (DataGridColumnDefinition | DataGridColumnDefinitionWithCustomCellFormatter<any>)[] = [
    {
      name: "Milestone",
      key: "milestoneLink",
      dataType: "string",
      formatter: "buttonLink",
      alignment: "left",
      filterable: true,
      minWidth: 300,
      sortable: true,
    },
    {
      name: "Assigned to",
      key: "avatar",
      dataType: "string",
      formatter: "custom",
      alignment: "center",
      customCellFormatter: AvatarCellFormatter,
      filterable: true,
      minWidth: 70,
      sortable: true,
    },
    {
      name: "Status",
      key: "status",
      dataType: "string",
      formatter: "stringWithEllipsis",
      alignment: "center",
      filterable: true,
      minWidth: 70,
      sortable: true,
    },
    {
      name: "Deadline",
      key: "deadlineDate",
      dataType: "string",
      formatter: "dateOnlyWithIndicator",
      alignment: "left",
      filterable: false,
      minWidth: 70,
      sortable: true,
    },
  ];

  if (!projectUuid) {
    columns.splice(1, 0, {
      name: "Project",
      key: "projectLink",
      dataType: "string",
      formatter: "link",
      alignment: "left",
      filterable: true,
      minWidth: 140,
      sortable: true,
    });

    columns.push({
      key: "viewTasksBoardIcon",
      name: "View tasks board",
      dataType: "string",
      formatter: "iconLink",
      alignment: "center",
      filterable: false,
      sortable: false,
      minWidth: 70,
    });
  }

  const formatData = useCallback((responseData: SearchWorkflowTasksResponse | undefined): ResultData[] => {
    return (
      responseData?.results?.map((d) => {
        const result = flattenObject(d) as ResultData & { avatar: { fullName?: string } };
        result.avatar = { fullName: d.assignee?.name };
        result.milestoneLink = {
          text: d.name,
          action: () => {
            setSelectedPanelTask(d);
            setShowWorkflowTaskPanel(true);
          },
        } as DataGridButtonLinkCellFormatterData;

        if (!projectUuid) {
          /*
            A general task has no workflow object — it stores its parent on objectType/objectUuid — so the
            link is built from whichever relation the task actually carries. The search returns no name for
            a general task's parent, so the object type stands in as the label.
          */
          const isGeneralTask = d.workflowUuid == null;
          const parentType = isGeneralTask ? d.objectType : d.workflowObjectType;
          const parentUuid = isGeneralTask ? d.objectUuid : d.workflowObjectUuid;
          const parentName = d.workflowObjectName ?? parentType;

          // Only Projects and Deals have a route here; anything else (e.g. a Landholding) stays unlinked
          // rather than pointing at a project page that does not exist.
          if (parentUuid && (parentType === "Deal" || parentType === "Project")) {
            const objectRoute =
              parentType === "Deal"
                ? getDealListingRoute(parentUuid, currentUserType)
                : getProjectDetailsByUuid(parentUuid, currentUserType, {
                    origin: isDashboard ? Origin.DashboardPage : Origin.ProjectTrackerPage,
                  });
            result.projectLink = {
              text: parentName,
              to: objectRoute,
              state: groupUuid
                ? { origin: { goBackText: "Back to landholding", from: location.pathname } as IStateOrigin }
                : undefined,
            } as LinkCellFormatterData;
          }

          // A general task has no workflow, so there is no tasks board to link to.
          if (d.workflowUuid) {
            result.viewTasksBoardIcon = {
              to: getWorkflowTasksBoardRoute(d.workflowUuid, currentUserType, {
                origin: isDashboard ? Origin.DashboardPage : Origin.ProjectTrackerPage,
              }),
              state: groupUuid
                ? { origin: { goBackText: "Back to landholding", from: location.pathname } as IStateOrigin }
                : undefined,
              icon: <ArrowheadRightDarkIcon />,
            } as IconLinkCellFormatterData;
          }
        }

        return result;
      }) || []
    );
  }, []);

  const onChange = async ({
    filtering,
    paging,
    sorting,
  }: CursorChangeProps): Promise<{
    resultData: ResultData[];
    paging: {
      pageSize: number;
      totalCount: number;
      startCursor: string;
      endCursor: string;
      hasNextPage: boolean;
      hasPreviousPage: boolean;
    };
  }> => {
    let data = {
      resultData: [] as ResultData[],
      paging: {
        pageSize: paging.pageSize,
        totalCount: 0,
        startCursor: "",
        endCursor: "",
        hasNextPage: false,
        hasPreviousPage: false,
      },
    };

    const filterCriteria = dataGridMapFilterCriteria(filtering);

    if (workflowUuid) {
      filterCriteria.workflowUuid = {
        operator: "in",
        value: [workflowUuid],
      };
    } else if (projectUuid) {
      filterCriteria.workflowObjectUuid = {
        operator: "eq",
        value: projectUuid,
      };
    }

    if (groupUuid) {
      filterCriteria.groups = {
        some: {
          groupUuid: {
            operator: "eq",
            value: groupUuid,
          },
        },
      };
    }

    filterCriteria.isMilestone = {
      operator: "eq",
      value: true,
    };
    filterCriteria.deadlineDate = {
      operator: "neq",
      value: null,
    };

    filterCriteria.status = {
      operator: "neq",
      value: TaskConstants.TASK_STATUS_COMPLETE,
    };
    if (filterCriteria.avatar) {
      filterCriteria.assignee = {
        name: filterCriteria.avatar,
      };
      delete filterCriteria.avatar;
    }
    if (filterCriteria.milestoneLink) {
      filterCriteria.name = filterCriteria.milestoneLink;
      filterCriteria.milestoneLink = undefined;
      delete filterCriteria.milestoneLink;
    }

    if (filterCriteria.projectLink) {
      filterCriteria.projectName = filterCriteria.projectLink;
      filterCriteria.projectLink = undefined;
      delete filterCriteria.projectLink;
    }

    const sortKeyMap: Record<string, string> = {
      milestoneLink: "name",
      avatar: "assignee.name",
      projectLink: "projectName",
    };

    await searchWorkflowTasks({
      paging: {
        limit: paging.pageSize,
        beforeCursor: paging.beforeCursor || null,
        afterCursor: paging.afterCursor || null,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      sort: sorting.map((sort: { key: any; direction: any }) => ({
        key: sortKeyMap[sort.key] || sort.key,
        direction: sort.direction,
      })),
      filter: { results: filterCriteria },
    })
      .then((response) => {
        data = {
          resultData: formatData(response.data),
          paging: {
            startCursor: response.data?.paging?.startCursor || "",
            endCursor: response.data?.paging?.endCursor || "",
            pageSize: paging.pageSize || 10,
            totalCount: response.data?.paging?.total || 0,
            hasNextPage: response.data?.paging?.hasNextPage || false,
            hasPreviousPage: response.data?.paging?.hasPreviousPage || false,
          },
        };
      })
      .finally(() => {
        setDataIsLoading(false);
      });

    return data;
  };

  return {
    columns,
    defaultSortingCriteria,
    dataIsLoading,
    onChange,
  };
};
